import { SortingState } from '@tanstack/react-table'
import { toast } from 'components/FSGToast'
import { camelCase, get } from 'lodash'
import { setDealsPerPage } from 'store/slices/deals'
import {
  addAccount,
  setAccounts,
  setNotificationSettings,
  setUser,
  setWallets,
  setMyDealsPerPage,
  setPayinAccounts,
  setDealTransactions,
  removeDealFromList,
  updateDeal,
  setKYCStatus,
} from 'store/slices/user'
import {
  setDealsTransactions,
  setDealsTransactionsPerPage,
  setWalletTransactions,
  setWalletTransactionsPerPage,
} from 'store/slices/wallet'
import {
  User,
  UpdateUserProfileRequest,
  NotificationSettings,
  ChangePassword,
  Account,
  CreateAccountModel,
  Deal,
  Wallet,
  DealTransaction,
  PaginationResponse,
  PayoutVerifyResponse,
  PayoutRequest,
  PayinAccount,
  MarketplaceDeal,
  BuyDeal,
  VerificationDeal,
  BaseError,
  PayoutResponse,
  WalletTransaction,
  OTPResponse,
  BuyDealVerificationResult,
  CADPayin,
} from 'types'
import { CreateDealRequest } from 'types/DealRequest'
import { KYCStatus } from 'types/KYCStatus'
import { emptySplitApi } from './emptySplit.api'
import { TOAST_ERROR_TIMEOUT, TOAST_TIMEOUT } from 'utils/constants'

const getSortingString = (sorting?: SortingState) =>
  sorting
    ?.map((item) => {
      const sortField = camelCase(`sort ${item.id}`)
      const order = item.desc ? 'DESC' : 'ASC'

      return `${sortField}=${order}`
    })
    .join('&')

const getFilterString = (filters?: { amountRange?: number[]; exchangeRate?: number }) => {
  if (!filters) return ''
  return Object.entries(filters)
    .filter(([key, value]) => value)
    .map(([key, value]) => {
      if (key === 'amountRange' && typeof value !== 'number') {
        return `minAmount=${value[0]}&maxAmount=${value[1]}`
      }
      if (key === 'exchangeRate' && typeof value === 'number') {
        return `rate=${value}`
      }
    })
    .join('&')
}

// Define a service using a base URL and expected endpoints
export const userApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<User, void>({
      query: () => ({
        url: 'users/me/profile',
        method: 'GET',
        responseHandler: (response) => {
          return response.json()
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(setUser(data))
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          console.error(err)
        }
      },
    }),
    updateUserPersonalInfo: builder.mutation<User, UpdateUserProfileRequest>({
      query: (body) => ({
        url: 'users/me/profile',
        method: 'PUT',
        body,
        responseHandler: (response) => {
          return response.json()
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(setUser(data))
          toast({
            title: 'User profile',
            description: 'User profile successfully updated',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'User profile',
            description: (err as BaseError).error.data?.message || 'User profile update failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        }
      },
    }),
    getNotificationSettings: builder.query<NotificationSettings, void>({
      query: () => ({
        url: 'users/me/notifications/settings',
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(setNotificationSettings(data))
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          console.error(err)
        }
      },
    }),
    updateNotificationSettings: builder.mutation<NotificationSettings, NotificationSettings>({
      query: (body) => ({
        url: 'users/me/notifications/settings',
        method: 'PUT',
        body,
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          dispatch(setNotificationSettings(data))
          toast({
            title: 'Notification settings',
            description: 'Notification settings successfully updated',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Notification settings',
            description:
              (err as BaseError).error.data?.message || 'Notification settings update failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    changePassword: builder.mutation<any, ChangePassword>({
      query: (body) => ({
        url: 'users/me/password',
        method: 'PUT',
        body,
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect

          toast({
            title: 'Notification settings',
            description: 'Notification settings successfully updated',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Notification settings',
            description:
              (err as BaseError).error.data?.message || 'Notification settings update failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    // Payouts API
    getAccounts: builder.query<{ accounts: Account[] }, void>({
      query: () => ({
        url: 'users/me/payouts/accounts',
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      providesTags: ['Accounts'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          // `onSuccess` side-effect
          dispatch(setAccounts(data))
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Accounts settings',
            description:
              (err as BaseError).error.data?.message || 'Accounts settings update failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    createAccount: builder.mutation<Account, CreateAccountModel>({
      query: (body) => ({
        url: 'users/me/payouts/accounts',
        method: 'POST',
        body,
        responseHandler: (response) => response.json(),
      }),
      invalidatesTags: ['Accounts'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(addAccount(data))
          toast({
            title: 'Payment accounts',
            description: 'Payment account successfully created',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Payment accounts',
            description:
              (err as BaseError).error.data?.message || 'Payment account creation failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    updateAccount: builder.mutation<Account, { body: CreateAccountModel; accountId: string }>({
      query: ({ body, accountId }) => ({
        url: `users/me/payouts/accounts/${accountId}`,
        method: 'PUT',
        body,
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(addAccount(data))
          if (!(data as unknown as OTPResponse).code) {
            toast({
              title: 'Payment accounts',
              description: 'Payment account successfully updated',
              status: 'success',
              duration: TOAST_TIMEOUT,
              isClosable: true,
              position: 'top',
            })
          }
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Payment accounts',
            description: (err as BaseError).error.data?.message || 'Payment account update failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    deleteAccount: builder.mutation<Account, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `users/me/payouts/accounts/${accountId}`,
        method: 'DELETE',
        responseHandler: (response) => response.json(),
      }),
      invalidatesTags: ['Accounts'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(addAccount(data))
          toast({
            title: 'Payment accounts',
            description: 'Payment account successfully deleted',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Payment accounts',
            description: (err as BaseError).error.data?.message || 'Payment account delete failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    // Deals API
    getMarketplaceDeals: builder.mutation<
      PaginationResponse<{ deals: MarketplaceDeal[] }>,
      {
        page: number
        pairId: string
        sorting?: SortingState
        filters?: { amountRange?: number[]; exchangeRate?: number }
      }
    >({
      query: ({ pairId, page, sorting, filters }) => {
        const sortingString = getSortingString(sorting)
        const filterString = getFilterString(filters)
        return {
          url: `users/me/deals/${pairId}/market_place?page=${page}&size=${20}&${
            sortingString || ''
          }&${filterString || ''}`,
          method: 'GET',
          responseHandler: (response) => response.json(),
        }
      },

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setDealsPerPage(data))
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Marketplace deals',
            description: (err as BaseError).error.data?.message || 'Marketplace deals fetch failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    buyDeal: builder.mutation<any, BuyDeal>({
      query: (body) => ({
        url: `users/me/deals/buy`,
        body,
        method: 'POST',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast({
            title: 'Marketplace',
            description: 'Deal accepted',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Marketplace',
            description: (err as BaseError).error.data?.message || 'Deal accept failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        }
      },
      invalidatesTags: ['Wallet'],
    }),

    getDeals: builder.query<
      PaginationResponse<{ deals: Deal[] }>,
      { page: number; completed: boolean; sorting?: SortingState }
    >({
      query: ({ page, completed, sorting }) => {
        const sortingString = getSortingString(sorting)
        const url =
          `users/me/deals?page=${page}&size=${20}&completed=${completed}` +
          (sorting ? `&${sortingString}` : '')
        return {
          url,
          method: 'GET',
          responseHandler: (response) => response.json(),
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setMyDealsPerPage(data))
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'My deals',
            description: (err as BaseError).error.data?.message || 'My deals fetch failed',
            status: 'error',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    getDealTransactionsById: builder.mutation<
      PaginationResponse<{ transactions: DealTransaction[] }>,
      { dealId: string }
    >({
      query: ({ dealId }) => ({
        url: `users/me/deals/${dealId}/transactions`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setDealTransactions({
              data: data.transactions,
              dealId: args.dealId,
            }),
          )
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'My deals transactions',
            description:
              (err as BaseError).error.data?.message || 'My deals transactions fetch failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    deleteDeal: builder.mutation<
      PaginationResponse<{ transactions: DealTransaction[] }>,
      { dealId: string }
    >({
      query: ({ dealId }) => ({
        url: `users/me/deals/${dealId}`,
        method: 'DELETE',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(removeDealFromList({ dealId: args.dealId }))
          toast({
            title: 'My deals',
            description: `You have successfully deleted your deal`,
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'My deals delete deal',
            description: (err as BaseError).error.data?.message || 'My deals delete deal failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
      invalidatesTags: ['Wallet'],
    }),
    updateDeal: builder.mutation<
      PaginationResponse<{ transactions: DealTransaction[] }>,
      { dealId: string; rate: number; dryRun?: boolean }
    >({
      query: ({ dealId, rate, dryRun = false }) => ({
        url: `users/me/deals/${dealId}?dryRun=${dryRun}`,
        method: 'PUT',
        body: { rate },
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          toast({
            title: 'My deals',
            description:
              args.dryRun && ((data as any).deals?.length || 0) > 0
                ? `Better deal is available`
                : `Deal rate is updated`,
            status: args.dryRun && (data as any).deals.length > 0 ? 'info' : 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          if (err && (err as BaseError).error.status === 400) {
            toast({
              title: 'Update deal',
              description:
                (err as BaseError).error?.data?.message || 'Update deal failed, try again',
              status: 'error',
              duration: TOAST_ERROR_TIMEOUT,
              isClosable: true,
              position: 'top',
            })
          } else {
            toast({
              title: 'Update deal',
              description:
                (err as BaseError).error?.data?.message || 'Update deal failed, try again',
              status: 'error',
              duration: TOAST_ERROR_TIMEOUT,
              isClosable: true,
              position: 'top',
            })
          }
          console.error(err)
        }
      },
    }),
    getDealById: builder.mutation<Deal, { dealId: string }>({
      query: ({ dealId }) => ({
        url: `users/me/deals/${dealId}`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(updateDeal(data))
        } catch (err) {
          // `onError` side-effect
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          console.error(err)
        }
      },
    }),
    createDeal: builder.mutation<any, CreateDealRequest>({
      query: (body) => ({
        url: 'users/me/deals',
        body,
        method: 'POST',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          console.error(err)
        }
      },
      invalidatesTags: ['Wallet', 'Banks'],
    }),
    verifyDeal: builder.mutation<
      { deals: VerificationDeal[]; commissionAmount: number; discount: number },
      CreateDealRequest
    >({
      query: (body) => ({
        url: 'users/me/deals/verify',
        body,
        method: 'POST',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
        } catch (err) {
          console.error(err)
        }
      },
    }),

    verifyBuyDeal: builder.mutation<BuyDealVerificationResult, CreateDealRequest>({
      query: (body) => ({
        url: 'users/me/deals/verify?isBuy=true',
        body,
        method: 'POST',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          toast({
            title: 'Verify deal',
            description: (err as BaseError).error?.data?.message || 'Verify deal failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),

    verifyBuyDealNotAuthed: builder.mutation<BuyDealVerificationResult, CreateDealRequest>({
      query: (body) => ({
        url: '/deals/commissions?isBuy=true',
        body,
        method: 'POST',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          toast({
            title: 'Verify deal',
            description: (err as BaseError).error?.data?.message || 'Verify deal failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),

    // Wallet API
    getWallet: builder.query<{ wallets: Wallet[] }, void>({
      query: () => ({
        url: 'users/me/wallets',
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setWallets(data.wallets))
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Wallet',
            description:
              (err as BaseError).error?.data?.message ||
              'Something went wrong, please contact administrator',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        }
      },
      providesTags: ['Wallet'],
    }),
    getWalletTransactions: builder.mutation<
      PaginationResponse<{ transactions: WalletTransaction[] }>,
      {
        page: number
        size?: number
        isMobile?: boolean
      }
    >({
      query: ({ page, size = 10 }) => ({
        url: `users/me/wallets/transactions?page=${page}&size=${size}`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (args.isMobile) {
            dispatch(setWalletTransactionsPerPage(data))
          }
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Wallet',
            description:
              (err as BaseError).error?.data?.message || 'Wallet transactions request failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          // `onError` side-effect
          console.error(err)
        }
      },
    }),
    //  builder.query<any, void>({
    //   query: () => ({
    //     url: 'users/me/wallets/transactions',
    //     method: 'GET',
    //     responseHandler: (response) => response.json(),
    //   }),
    //   async onQueryStarted(_, { dispatch, queryFulfilled }) {
    //     try {
    //       const { data } = await queryFulfilled
    //     } catch (err) {
    //       if (err && (err as BaseError).error.status === 401) {
    //         return
    //       }
    //       toast({
    //         title: 'Wallet',
    //         description: 'Something went wrong, please contact administrator',
    //         status: 'error',
    //         duration: TOAST_TIMEOUT,
    //         isClosable: true,
    //         position: 'top',
    //       })
    //       console.error(err)
    //     }
    //   },
    // }),
    // getDealsTransactions: builder.query<
    //   PaginationResponse<{ transactions: DealTransaction[] }>,
    //   {
    //     page: number
    //     size?: number
    //     isMobile?: boolean
    //   }
    // >({
    //   query: ({ page, size = 10 }) => ({
    //     url: `users/me/wallets/deals/transactions?page=${page}&size=${size}`,
    //     method: 'GET',
    //     responseHandler: (response) => response.json(),
    //   }),
    //   async onQueryStarted(args, { dispatch, queryFulfilled }) {
    //     try {
    //       const { data } = await queryFulfilled
    //       if (args.isMobile) {
    //         dispatch(setDealsTransactions(data))
    //       }
    //     } catch (err) {
    //       if (err && (err as BaseError).error.status === 401) {
    //         return
    //       }
    //       toast({
    //         title: 'Wallet',
    //         description: 'Something went wrong, please contact administrator',
    //         status: 'error',
    //         duration: TOAST_TIMEOUT,
    //         isClosable: true,
    //         position: 'top',
    //       })
    //       // `onError` side-effect
    //       console.error(err)
    //     }
    //   },
    // }),

    getDealsTransactions: builder.mutation<
      PaginationResponse<{ transactions: DealTransaction[] }>,
      {
        page: number
        size?: number
        isMobile?: boolean
      }
    >({
      query: ({ page, size = 10 }) => ({
        url: `users/me/wallets/deals/transactions?page=${page}&size=${size}`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (args.isMobile) {
            dispatch(setDealsTransactionsPerPage(data))
          }
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Wallet',
            description:
              (err as BaseError).error?.data?.message || 'Deals transactions request failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          // `onError` side-effect
          console.error(err)
        }
      },
    }),

    verifyPayout: builder.mutation<PayoutVerifyResponse, PayoutRequest>({
      query: (body) => ({
        url: `users/me/payouts/verify`,
        method: 'POST',
        body,
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Wallet',
            description: (err as BaseError).error?.data?.message || 'Verify payout failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    payouts: builder.mutation<PayoutResponse, PayoutRequest>({
      query: (body) => ({
        url: `users/me/payouts`,
        method: 'POST',
        body,
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast({
            title: 'Wallet payout',
            description: data.message,
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          toast({
            title: 'Wallet payout',
            description: (err as BaseError).error?.data?.message || 'Payout request failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          // `onError` side-effect
          console.error(err)
        }
      },
      invalidatesTags: ['Wallet'],
    }),
    getPayinAccounts: builder.query<{ accounts: PayinAccount[] }, void>({
      query: (body) => ({
        url: `users/me/payins/accounts`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setPayinAccounts(data))
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          // `onError` side-effect
          console.error(err)
        }
      },
    }),
    resolveCADPayin: builder.mutation<any, CADPayin>({
      query: (body) => ({
        url: `users/me/payins`,
        method: 'POST',
        body,
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast({
            title: 'Wallet payout',
            description: 'Money will be added to your wallet in 20 minutes',
            status: 'success',
            duration: TOAST_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          if (err && (err as BaseError).error.status === 400) {
            toast({
              title: 'Add funds',
              description: (err as BaseError).error?.data?.message || 'CAD payin request failed',
              status: 'error',
              duration: TOAST_ERROR_TIMEOUT,
              isClosable: true,
              position: 'top',
            })
          } else {
            toast({
              title: 'Add funds',
              description: (err as BaseError).error?.data?.message || 'CAD payin request failed',
              status: 'error',
              duration: TOAST_ERROR_TIMEOUT,
              isClosable: true,
              position: 'top',
            })
          }
          // `onError` side-effect
          console.error(err)
        }
      },
    }),

    // KYC integrations
    getKYCStatus: builder.query<KYCStatus, void>({
      query: (body) => ({
        url: `users/me/kycs/status`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setKYCStatus(data))
        } catch (err) {
          if (err && (err as BaseError).error.status === 401) {
            return
          }
          // `onError` side-effect
          console.error(err)
        }
      },
    }),
    getDealsNotAuthorized: builder.mutation<
      PaginationResponse<{ deals: MarketplaceDeal[] }>,
      {
        page: number
        pairId: string
        sorting?: SortingState
        filters?: { amountRange?: number[]; exchangeRate?: number }
      }
    >({
      query: ({ page, pairId, sorting, filters }) => {
        const sortingString = getSortingString(sorting)
        const filterString = getFilterString(filters)
        return {
          url: `/deals/${pairId}/marketplace?page=${page}&size=${20}&${sortingString || ''}&${
            filterString || ''
          }`,
          method: 'GET',
          responseHandler: (response) => response.json(),
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setDealsPerPage(data))
        } catch (err) {
          toast({
            title: 'Marketplace deals',
            description: (err as BaseError).error.data?.message || 'Marketplace deals fetch failed',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
          console.error(err)
        }
      },
    }),
    getDealByIdNotAuthenticated: builder.mutation<MarketplaceDeal, { dealId: string }>({
      query: ({ dealId }) => ({
        url: `/deals/${dealId}`,
        method: 'GET',
        responseHandler: (response) => response.json(),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
        } catch (err) {
          console.error(err)
        }
      },
    }),
  }),
  overrideExisting: false,
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useUpdateUserPersonalInfoMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useChangePasswordMutation,
  useGetAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  useCreateDealMutation,
  useGetWalletQuery,
  useGetDealsQuery,
  useGetDealsTransactionsMutation,
  useGetWalletTransactionsMutation,
  useVerifyPayoutMutation,
  usePayoutsMutation,
  useGetPayinAccountsQuery,
  useGetDealTransactionsByIdMutation,
  useDeleteDealMutation,
  useUpdateDealMutation,
  useGetDealByIdMutation,
  useGetMarketplaceDealsMutation,
  useBuyDealMutation,
  useVerifyDealMutation,
  useVerifyBuyDealMutation,
  useVerifyBuyDealNotAuthedMutation,
  useResolveCADPayinMutation,
  useGetKYCStatusQuery,
  useGetDealsNotAuthorizedMutation,
  useGetDealByIdNotAuthenticatedMutation,
} = userApi
